import Controller from '@ember/controller';
import { isBlank } from '@ember/utils';
import {
  ConversionTimeframe,
  ConversionType,
  createConversionGroupCondition,
  ModelWithConversion,
} from 'cio/utils/conversions';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';
import GroupCondition from 'cio/app-models/conditions/group';
import { Filters } from 'cio/utils/filters';

export const DEFAULT_CONVERSION_WINDOW = 60 * 60 * 24 * 7; // 7 days
interface ConversionSnapshot {
  conversion: boolean | null;
  conversionType: string;
  conversionWindow: number;
  conversionAction: string;
  conversionSegmentId: string | null;
  conversionEventName: string | null;
  conversionEventFilters: Filters | null;
}

export default class ConversionsController extends Controller {
  @service environment!: EnvironmentService;

  declare model: ModelWithConversion;

  conversionActions = [
    { value: 'receiving', label: 'receiving' },
    { value: 'opening', label: 'opening' },
    { value: 'clicking', label: 'clicking a tracked link in' },
  ];

  readonly maxTimeframe = 60 * 60 * 24 * 90; // 90 days
  readonly allowAnyTimeframe: boolean = false;
  conversionSnapshot: ConversionSnapshot | null = null;

  @tracked eventConversionCondition: GroupCondition | null = null;
  @tracked selectedTimeframeType = ConversionTimeframe.Within;

  private createSnapshot(): ConversionSnapshot {
    let { model } = this;

    return {
      conversion: model.conversion,
      conversionType: model.conversionType,
      conversionAction: model.conversionAction,
      conversionSegmentId: model.conversionSegmentId,
      conversionEventName: model.conversionEventName,
      conversionEventFilters: model.conversionEventFilters,
      conversionWindow: model.conversionWindow,
    };
  }

  private applySnapshot(snapshot: ConversionSnapshot | null) {
    if (snapshot != null) {
      this.model.setProperties({
        ...snapshot,
      });
    }
  }

  private createNewAndApplyCurrentSnapshot() {
    // Create a new snapshot to be used when switching back
    let nextSnapshot = this.createSnapshot();

    // Apply the current snapshot reverting conversion settings to
    // the previous state
    this.applySnapshot(this.conversionSnapshot);

    // Update current snapshot
    this.conversionSnapshot = nextSnapshot;
  }

  private enableConversion() {
    let { model } = this;

    model.set('conversion', true);

    if (isBlank(model.conversionType)) {
      model.set('conversionType', ConversionType.PerformEvent);
    }

    if (!this.allowAnyTimeframe && !model.conversionWindow) {
      model.set('conversionWindow', DEFAULT_CONVERSION_WINDOW);
    }
  }

  private disableConversion() {
    let { model } = this;

    model.setProperties({
      conversion: false,
      conversionAction: 'receiving',
      conversionSegmentId: null,
      conversionEventName: null,
      conversionEventFilters: null,
    });
  }

  resetConversionSnapshot() {
    this.conversionSnapshot = null;
  }

  createGroupCondition(filters: Filters | null) {
    if (filters != null) {
      this.eventConversionCondition = createConversionGroupCondition(this.model);
    } else {
      this.eventConversionCondition = null;
    }
  }

  @action
  onEnableConversion() {
    this.createNewAndApplyCurrentSnapshot();
    this.enableConversion();
  }

  @action
  onDisableConversion() {
    this.createNewAndApplyCurrentSnapshot();
    this.disableConversion();
  }

  @action
  onReset() {
    this.model.rollbackAttributes();
  }

  @action
  onSelectTimeframeType(type: ConversionTimeframe) {
    this.selectedTimeframeType = type;
  }
}
